import { Component, OnInit, ElementRef, ViewEncapsulation } from '@angular/core';
import { GridViewManager } from 'src/app/core/model/grid-view-manager';
import { BillableEdit } from '../model/billableEdit';
import { BillableItem } from './../../core/model/billable-item';
import { BillableService } from 'src/app/core/service/billable.service';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { AppConfigService } from 'src/app/core/service/app-config.service';
import { SearchHelperService } from 'src/app/core/utility/search-helper.service';
import { DataStateChangeEvent } from '@progress/kendo-angular-grid';
import { CompositeFilterDescriptor, GroupDescriptor, filterBy } from '@progress/kendo-data-query';
import { TranslateService } from '@ngx-translate/core';
import copyToClipboard from 'copy-to-clipboard';
import { ToastrService } from 'ngx-toastr';
import { saveAs } from '@progress/kendo-file-saver';
import { FileRestrictions, UploadEvent } from '@progress/kendo-angular-upload';
import { CustomFile } from 'src/app/message/model/custom-file';
import { sortBy } from 'lodash';
import { AccumulatorService } from 'src/app/core/service/accumulator.service';
import { DictionaryService } from 'src/app/core/service/dictionary.service';
import { NGXLogger } from 'ngx-logger';
import { lastValueFrom } from 'rxjs';
import { ExtensionService } from 'src/app/core/service/extension.service';
import { SettingService } from 'src/app/core/service/setting.service';
import { Base64 } from 'js-base64';

@Component({
  selector: 'app-billable-list',
  templateUrl: './billable-list.component.html',
  encapsulation: ViewEncapsulation.None,
  styles: [`
    .channel-message {
      width: 800px !important;
      min-width: 800px !important;
    }
    .billable-max {
      max-height: 600px !important;
      overflow: overlay !important;
    }`
  ]
})
export class BillableListComponent implements OnInit
{
  public pageSizes = [5, 10, 25, 50, 100];
  public view: GridViewManager;
  public deleteDialogOpened = false;
  public enableAdvanced = false;
  public count: number;
  public isNew = false;
  public editDataItem: BillableEdit;
  public selectedBillable: BillableEdit;
  public filterText = '';
  public channelsList: string;
  public billableServiceInUse = false;
  public billableServiceInUseChannelsList: string;
  public billableServiceCannotDelete: string;
  public billableServiceNotInUse: string;
  public invalidFileExtensionText: string;
  public feeSchedules: any;
  public feeCategories: any;
  public accumulators: any[] = [];
  public accumulatorNotFound: string;
  public feeCategoryNotFound: string;
  public feeScheduleNotFound: string;
  public successfulImport: string;
  public importedBillableServicesToSave: BillableEdit[] = [];
  public clientExtensions = [];
  public diagnosisLookupNotFound: string;
  public patientLabel: string;
  public allBillableServices = [];
  public importing = false;

  public fileImportRestrictions: FileRestrictions =
    {
      allowedExtensions: ['.json']
    };

  constructor(private billableService: BillableService,
    private messageHandlerService: MessageHandlerService,
    private appConfigService: AppConfigService,
    private searchHelperService: SearchHelperService,
    private translate: TranslateService,
    private toastrService: ToastrService,
    private elem: ElementRef,
    private accumulatorService: AccumulatorService,
    private dictionaryService: DictionaryService,
    private logger: NGXLogger,
    private extensionService: ExtensionService,
    private settingService: SettingService)
  {
  }

  public ngOnInit(): void
  {
    this.view = new GridViewManager(this.appConfigService.getConfig().pageSize);
    this.loadGridData();
    this.translate.get(['BILLABLE-SERVICE-NOT-IN-USE', 'BILLABLE-SERVICE-CANNOT-DELETE', 'BILLABLE-SERVICE-IN-USE-CANNOT-DELETE', 'INVALID-FILE-TYPE', 'ACCUMULATOR-NOT-FOUND',
      'FEE-CATEGORY-NOT-FOUND', 'FEE-SCHEDULE-NOT-FOUND', 'BILLABLE-SERVICES-IMPORTED-SUCCESSFULLY', 'DIAGNOSIS-LOOKUP-NOT-FOUND'])
      .subscribe({
        next: (res) =>
        {
          this.billableServiceNotInUse = res['BILLABLE-SERVICE-NOT-IN-USE'];
          this.billableServiceCannotDelete = res['BILLABLE-SERVICE-CANNOT-DELETE'];
          this.billableServiceInUseChannelsList = res['BILLABLE-SERVICE-IN-USE-CANNOT-DELETE'];
          this.invalidFileExtensionText = res['INVALID-FILE-TYPE'];
          this.accumulatorNotFound = res['ACCUMULATOR-NOT-FOUND'];
          this.feeCategoryNotFound = res['FEE-CATEGORY-NOT-FOUND'];
          this.feeScheduleNotFound = res['FEE-SCHEDULE-NOT-FOUND'];
          this.successfulImport = res['BILLABLE-SERVICES-IMPORTED-SUCCESSFULLY'];
          this.diagnosisLookupNotFound = res['DIAGNOSIS-LOOKUP-NOT-FOUND'];
        }
      });
    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        this.patientLabel = sessionSetting.patientLabel;
      }
    });
  }

  /**
   * Closes a component
   *
   * @param component Name of component to be closed.
   */
  public close(component: string): void
  {
    this[component + 'Opened'] = false;
  }

  /**
   * Opens a component
   *
   * @param component Name of component to be opened.
   */
  public open(component: string): void
  {
    this[component + 'Opened'] = true;
  }

  /**
   * Button action handler.
   *
   * @param confirm Confirm the action: yes/no
   */
  public deleteAction(confirm: boolean): void
  {
    if (confirm)
    {
      // Check if in use
      const billableId = this.selectedBillable.id;
      if (this.billableServiceInUse)
      {
        this.toastrService.error(this.billableServiceInUseChannelsList, this.billableServiceCannotDelete,
          {
            enableHtml: true,
            disableTimeOut: true,
            tapToDismiss: false,
            toastClass: 'ngx-toastr toast-error channel-message'
          });
        this.copyChannelsList(this.channelsList.replace(/<br\/>/g, '\r\n'));
      }
      else
      {
        // Proceed with the deletion
        this.billableService.deleteBillableService(billableId).subscribe({
          next: () =>
          {
            this.view.data = this.view.data.filter(e => e.id !== billableId);
            this.view.refreshView();
            this.count = this.view.data.length;
            this.messageHandlerService.displaySuccess('BILLABLE-SERVICE-DELETED-SUCCESSFULLY');
          },
          error: (errorResponse) =>
          {
            this.messageHandlerService.displayError(errorResponse);
          }
        });
      }
    }
    // post-deletion cleanup and refresh grid
    this.close('deleteDialog');
    this.selectedBillable = null;
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public dataStateChange(state: DataStateChangeEvent): void
  {
    this.view.setState(state);
  }

  /**
   * Group change event handler.
   *
   * @param groups List of GroupDescriptors
   */
  public groupChange(groups: GroupDescriptor[]): void
  {
    this.view.setGroup(groups);
  }

  /**
   * Sets the user to delete, and opens the delete user dialog.
   */
  public deleteHandler(id: number): void
  {
    this.billableService.getBillableServiceById(id).subscribe({
      next: (data) =>
      {
        this.selectedBillable = data as BillableEdit;
        this.billableService.billableServiceInUse(this.selectedBillable.id).subscribe({
          next: (inUseResponse) =>
          {
            this.billableServiceInUse = inUseResponse;
            this.billableService.getChannelsPerBillableService(this.selectedBillable.id).subscribe({
              next: (channels) =>
              {
                if (channels.length > 0)
                {
                  let channelsText = '';
                  channels.forEach(c => channelsText += c + '<br/>');
                  this.channelsList = channelsText;
                  this.billableServiceInUseChannelsList = this.billableServiceInUseChannelsList.replace('{{channelsList}}', channelsText);
                }

              },
              error: (errorResponse) =>
              {
                this.messageHandlerService.displayError(errorResponse);
              }
            });
            this.open('deleteDialog');
          },
          error: (errorResponse) =>
          {
            this.messageHandlerService.displayError(errorResponse);
          }
        });
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public copyChannelsList(channelsText: string): void
  {
    copyToClipboard(channelsText);
  }

  public addHandler(): void
  {
    this.isNew = true;
    this.editDataItem = new BillableEdit();
  }

  public editHandler(id: number): void
  {
    this.isNew = false;
    this.billableService.getBillableServiceById(id).subscribe({
      next: (data) =>
      {
        this.editDataItem = data;
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public saveChangesHandler(billable: BillableEdit): void
  {
    this.billableService.saveBillableService(billable, this.isNew, billable.id).subscribe({
      next: (data) =>
      {
        // Must convert the lastModifiedOn into Date object
        data.lastModifiedOn = new Date(data.lastModifiedOn);
        this.view.addOrUpdateGridRow(data);
        this.count = this.view.data.length;  // Update count

        this.editDataItem = undefined;
        this.messageHandlerService.displaySuccess('BILLABLE-SERVICE-SAVED-SUCCESSFULLY');
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public cancelChangesHandler(): void
  {
    this.editDataItem = undefined;
  }

  /**
   * Filtering by search field
   */
  public filterChange(filter: CompositeFilterDescriptor): void
  {
    this.view.state.filter = filter;
    this.view.gridData.data = filterBy(this.view.data, filter);
    this.count = this.view.gridData.data.length;
    this.view.refreshView();
  }

  public searchFieldChange(filterText: string): void
  {
    this.view.state.skip = 0;
    const root = this.searchHelperService.fieldChange(filterText, this.view.state);
    this.filterText = filterText;
    this.filterChange(root);
  }

  public exportToJson()
  {
    const billableServices = [];
    let processedCount = 0;
    this.view.data.forEach(b =>
    {
      this.billableService.getBillableServiceById(b.id).subscribe({
        next: (data: any) =>
        {
          processedCount++;
          billableServices.push(data);
          if (processedCount === this.view.data.length)
          {
            const fileName = 'BillableServices';
            const based64Data = Base64.encode(JSON.stringify(billableServices));
            saveAs(`data:application/json;base64,${based64Data}`, `${fileName}.json`);
          }
        }
      });
    });
  }

  public importFromJson()
  {
    this.dictionaryService.getCachedFeeScheduless().subscribe({
      next: (data) =>
      {
        this.feeSchedules = data;
      }
    });
    this.dictionaryService.getCachedFeeCategories().subscribe({
      next: (data) =>
      {
        this.feeCategories = data;
      }
    });
    this.accumulatorService.getAccumulatorList().subscribe({
      next: (data) =>
      {
        this.accumulators = data;
      }
    });
    this.extensionService.getExtensionsByCategory('PATIENT', true, true).subscribe({
      next: (data) =>
      {
        this.clientExtensions = data;
      }
    });
    const uploadButton = this.elem.nativeElement.querySelectorAll('input[name="files"]');
    uploadButton[0].click();
  }

  public onFileImportChanged(event: UploadEvent): void
  {
    event.files.forEach((file) =>
    {
      // Validate file extension
      let validExtension = false;
      this.fileImportRestrictions.allowedExtensions.forEach((ext: string) =>
      {
        if (file.name.toLowerCase().indexOf(ext) !== -1)
        {
          validExtension = true;
        }
      });
      if (!validExtension)
      {
        this.messageHandlerService.displayDirectMessage('error',
          this.invalidFileExtensionText.replace('{{value}}', this.fileImportRestrictions.allowedExtensions.join()));
        return;
      }

      const customFile = new CustomFile();
      customFile.name = file.name;
      customFile.type = file.rawFile.type;

      this.billableService.getBillableServiceList().subscribe({
        next: (data) =>
        {
          this.allBillableServices = data;
          this.setImportFileFromBlob(customFile, file.rawFile);
        }
      });
    });
  }

  private async readFileAsync(file: Blob): Promise<string>
  {
    return new Promise((resolve, reject) =>
    {
      const reader = new FileReader();

      reader.onload = () =>
      {
        // Resolve the promise with the result (base64 data)
        resolve(reader.result as string);
      };

      reader.onerror = (error) =>
      {
        // Reject the promise if there's an error
        reject(error);
      };

      reader.readAsDataURL(file);
    });
  }

  private async setImportFileFromBlob(customFile: CustomFile, file: Blob): Promise<void>
  {
    try
    {
      this.importing = true;

      // Await the result of readFileAsync (base64 data)
      const base64: string = await this.readFileAsync(file);
      customFile.data = base64.split('base64,')[1];
      const importedData = JSON.parse(Base64.decode(customFile.data));

      let messages = '';
      this.importedBillableServicesToSave = [];

      for (let index = 0; index < importedData.length; index++)      
      {
        const data = importedData[index];
        const isLastIteration = index === importedData.length - 1;

        try
        {
          // Await the result of billableService.existsBillableServiceByName
          const response = await this.billableService.existsBillableServiceByNameSynchronous(data.name);

          const isNewBillableService = response === null;
          const existingBillableServiceId = response === null ? null : response;

          if (data.name)
          {
            const billable: BillableEdit = new BillableEdit();
            billable.id = existingBillableServiceId;
            billable.description = data.description;
            billable.enabled = data.enabled;
            billable.manualAmount = data.manualAmount;
            billable.informationalOnly = data.informationalOnly;
            billable.name = data.name;
            billable.externalCode = data.externalCode;
            billable.version = isNewBillableService ? 0 : data.version;

            // Accumulator
            if (data.accumulator !== null)
            {
              const accumulator = this.accumulators.filter(x => x.name === data.accumulator.name)[0];
              if (accumulator === undefined) // accumulator not found, show message but import can still go through
              {
                messages = messages.concat(this.accumulatorNotFound.replace('{{billableServiceName}}', data.name).replace('{{accumulatorName}}', data.accumulator.name)).concat('<br/><br/>');
              }
              else
              {
                billable.accumulator = accumulator;
              }
            }

            // Fee Category
            if (data.feeCategory !== null)
            {
              const feeCategory = this.feeCategories.filter(x => x.displayName === data.feeCategory.displayName)[0];
              if (feeCategory === undefined) // fee category not found, do not import this billable service
              {
                messages = messages.concat(this.feeCategoryNotFound.replace('{{billableServiceName}}', data.name).replace('{{feeCategoryName}}', data.feeCategory.displayName)).concat('<br/><br/>');
                if (isLastIteration)
                {
                  if (this.importedBillableServicesToSave.length > 0)
                  {
                    await this.saveImportedBillableServices(this.importedBillableServicesToSave);
                  }
                  const title = this.importedBillableServicesToSave.length > 0 ? this.successfulImport : null;
                  const message = '<div class="billable-max">'.concat(messages).concat('</div>');
                  this.toastrService.success(message, title,
                    {
                      enableHtml: true,
                      disableTimeOut: true,
                      tapToDismiss: false,
                      toastClass: 'ngx-toastr toast-success channel-message'
                    });
                }
                continue;
              }
              else
              {
                billable.feeCategory = feeCategory;
              }
            }

            // Fee Schedule
            if (data.feeSchedule !== null)
            {
              const feeSchedule = this.feeSchedules.filter(x => x.displayName === data.feeSchedule.displayName)[0];
              if (feeSchedule === undefined) // fee schedule not found, do not import this billable service
              {
                messages = messages.concat(this.feeScheduleNotFound.replace('{{billableServiceName}}', data.name).replace('{{feeScheduleName}}', data.feeSchedule.displayName)).concat('<br/><br/>');
                if (isLastIteration)
                {
                  if (this.importedBillableServicesToSave.length > 0)
                  {
                    await this.saveImportedBillableServices(this.importedBillableServicesToSave);
                  }
                  const title = this.importedBillableServicesToSave.length > 0 ? this.successfulImport : null;
                  const message = '<div class="billable-max">'.concat(messages).concat('</div>');
                  this.toastrService.success(message, title,
                    {
                      enableHtml: true,
                      disableTimeOut: true,
                      tapToDismiss: false,
                      toastClass: 'ngx-toastr toast-success channel-message'
                    });
                }
                continue;
              }
              else
              {
                billable.feeSchedule = feeSchedule;
              }
            }

            // Set Billable Items
            const billableItemsToSave = [];
            if (data.billableItems && data.billableItems.length > 0)
            {
              for (let index2 = 0; index2 < data.billableItems.length; index2++)      
              {
                const item = data.billableItems[index2];
                const isLastIteration2 = index2 === data.billableItems.length - 1;
                if (isNewBillableService == true) // new billable item for new billable service
                {
                  // Diagnosis Lookup (Client Extensions)
                  const newExtensions = [];
                  if ((item.extensions !== null) && (item.extensions.length > 0))
                  {
                    for (let index3 = 0; index3 < item.extensions.length; index3++)      
                    {
                      const importedExtension = item.extensions[index3];
                      const extension = this.clientExtensions.filter(x => x.name === importedExtension.name)[0];
                      if (extension === undefined) // client extension not found, show message but import can still go through
                      {
                        messages = messages.concat(this.diagnosisLookupNotFound.replace('{{billableServiceName}}', data.name).replace('{{diagnosisLookupName}}', importedExtension.name).replace('{{patientLabel}}', this.patientLabel)).concat('<br/><br/>');
                      }
                      else
                      {
                        newExtensions.push(extension);
                      }
                    }
                  }

                  const newBillableItem: BillableItem = new BillableItem();
                  newBillableItem.code = item.code;
                  newBillableItem.diagnosisCode = item.diagnosisCode;
                  newBillableItem.modifierCode = item.modifierCode;
                  newBillableItem.modifierCode2 = item.modifierCode2;
                  newBillableItem.modifierCode3 = item.modifierCode3;
                  newBillableItem.rate = item.rate;
                  newBillableItem.from = item.from;
                  newBillableItem.to = item.to;
                  newBillableItem.version = 0;
                  newBillableItem.extensions = newExtensions;
                  billableItemsToSave.push(newBillableItem);
                  billable.billableItems = sortBy(billableItemsToSave, ['from']);
                  if (isLastIteration2)
                  {
                    this.importedBillableServicesToSave.push(billable);
                  }
                }
                else
                {
                  const result = await this.billableService.billableItemByCodeExistsSynchronous(Number(existingBillableServiceId), item.code);
                  if (result !== null) 
                  {
                    // Diagnosis Lookup (Client Extensions)
                    const newExtensions = [];
                    if ((item.extensions !== null) && (item.extensions.length > 0))
                    {
                      for (let index3 = 0; index3 < item.extensions.length; index3++)      
                      {
                        const importedExtension = item.extensions[index3];
                        const extension = this.clientExtensions.filter(x => x.name === importedExtension.name)[0];
                        if (extension === undefined) // client extension not found, show message but import can still go through
                        {
                          messages = messages.concat(this.diagnosisLookupNotFound.replace('{{billableServiceName}}', data.name).replace('{{diagnosisLookupName}}', importedExtension.name).replace('{{patientLabel}}', this.patientLabel)).concat('<br/><br/>');
                        }
                        else
                        {
                          newExtensions.push(extension);
                        }
                      }
                    }

                    // Billable item with Code 'item.code' exists, update billable item
                    const updatedBillableItem: BillableItem = new BillableItem();
                    updatedBillableItem.id = Number(result);
                    updatedBillableItem.code = item.code;
                    updatedBillableItem.diagnosisCode = item.diagnosisCode;
                    updatedBillableItem.modifierCode = item.modifierCode;
                    updatedBillableItem.modifierCode2 = item.modifierCode2;
                    updatedBillableItem.modifierCode3 = item.modifierCode3;
                    updatedBillableItem.rate = item.rate;
                    updatedBillableItem.from = item.from;
                    updatedBillableItem.to = item.to;
                    updatedBillableItem.version = item.version;
                    updatedBillableItem.extensions = newExtensions;
                    updatedBillableItem.billableId = Number(existingBillableServiceId);
                    billableItemsToSave.push(updatedBillableItem);
                    billable.billableItems = sortBy(billableItemsToSave, ['from']);
                    if (isLastIteration2) // processed all new data.billableItems
                    {
                      this.importedBillableServicesToSave.push(billable);
                    }
                  }
                  else // new billable item for existing billable service
                  {
                    // Diagnosis Lookup (Client Extensions)
                    const newExtensions = [];
                    if ((item.extensions !== null) && (item.extensions.length > 0))
                    {
                      for (let index3 = 0; index3 < item.extensions.length; index3++)      
                      {
                        const importedExtension = item.extensions[index3];
                        const extension = this.clientExtensions.filter(x => x.name === importedExtension.name)[0];
                        if (extension === undefined) // client extension not found, show message but import can still go through
                        {
                          messages = messages.concat(this.diagnosisLookupNotFound.replace('{{billableServiceName}}', data.name).replace('{{diagnosisLookupName}}', importedExtension.name).replace('{{patientLabel}}', this.patientLabel)).concat('<br/><br/>');
                        }
                        else
                        {
                          newExtensions.push(extension);
                        }
                      }
                    }

                    // Billable item with Code 'item.code' does not exist, create new billable item for existing billable service
                    const newBillableItem: BillableItem = new BillableItem();
                    newBillableItem.id = null;
                    newBillableItem.code = item.code;
                    newBillableItem.diagnosisCode = item.diagnosisCode;
                    newBillableItem.modifierCode = item.modifierCode;
                    newBillableItem.modifierCode2 = item.modifierCode2;
                    newBillableItem.modifierCode3 = item.modifierCode3;
                    newBillableItem.rate = item.rate;
                    newBillableItem.from = item.from;
                    newBillableItem.to = item.to;
                    newBillableItem.version = 0;
                    newBillableItem.extensions = newExtensions;
                    newBillableItem.billableId = Number(existingBillableServiceId);
                    billableItemsToSave.push(newBillableItem);
                    billable.billableItems = sortBy(billableItemsToSave, ['from']);
                    if (isLastIteration2)
                    {
                      this.importedBillableServicesToSave.push(billable);
                    }
                  }
                }
              }
            }
            else // no billable items
            {
              this.importedBillableServicesToSave.push(billable);
            }

            if (isLastIteration)
            {
              if (this.importedBillableServicesToSave.length > 0)
              {
                await this.saveImportedBillableServices(this.importedBillableServicesToSave);
              }
              if ((this.importedBillableServicesToSave.length > 0) || (messages !== ''))
              {
                const title = this.importedBillableServicesToSave.length > 0 ? this.successfulImport : null;
                const message = '<div class="billable-max">'.concat(messages).concat('</div>');
                this.toastrService.success(messages !== '' ? message : '', title,
                  {
                    enableHtml: true,
                    disableTimeOut: true,
                    tapToDismiss: false,
                    toastClass: 'ngx-toastr toast-success channel-message'
                  });
              }
            }
          }
        } catch (error)
        {
          // Handle errors for each async call (e.g., if existsBillableServiceByName fails)          
          this.logger.error(`Error checking billable service for name: ${data.name}`, error);
        }
      }
    } catch (error)
    {
      // Handle errors from file reading or JSON parsing
      this.logger.error('Error reading or processing file:', error);
    }
  }

  private async saveImportedBillableServices(billableServices: BillableEdit[]): Promise<void>
  {
    try
    {
      // Updating billable services and items versions
      billableServices.forEach((serviceToSave: BillableEdit) =>
      {
        const currentBillableService = this.allBillableServices.find((x: BillableEdit) => x.name === serviceToSave.name);
        if (currentBillableService)
        {
          if (currentBillableService.version !== serviceToSave.version) 
          {
            serviceToSave.version = currentBillableService.version;
          }
          if (serviceToSave.billableItems && currentBillableService.billableItems)
          {
            serviceToSave.billableItems.forEach((x: any) =>
            {
              const itemFound = currentBillableService.billableItems.find((y: any) => y.code === x.code);
              if (itemFound && itemFound.version != x.version)
              {
                x.version = itemFound.version;
              }
            });
          }
        }
      });
      await lastValueFrom(this.billableService.saveBillableServices(billableServices));
      this.loadGridData();
      this.editDataItem = undefined;
      this.importing = false;
    } catch (errorResponse)
    {
      this.messageHandlerService.displayError(errorResponse);
    }
  }

  private loadGridData(): void
  {
    this.view.startLoading();
    this.billableService.getBillableServiceList().subscribe({
      next: (data) =>
      {
        data.forEach(d => { d.lastModifiedOn = new Date(d.lastModifiedOn); });
        this.count = data.length;
        this.view.setData(data);
        this.view.setSort([{ field: 'name', dir: 'asc' }]);
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      },
      complete: () =>
      {
        this.view.stopLoading();
      }
    });
  }
}
